import { useEffect, useMemo, useState } from "react";

type BookingStatus =
  | "REQUESTED"
  | "AWAITING_ACCEPTANCE"
  | "ACCEPTED"
  | "AUTOACCEPTED"
  | "REJECTED"
  | "CANCELLED_BY_CUSTOMER"
  | "CANCELLED_BY_PROVIDER"
  | "IN_SERVICE"
  | "COMPLETED_PENDING_CONFIRMATION"
  | "COMPLETED_ASSUMED"
  | "COMPLETED_CONFIRMED"
  | "NO_SHOW"
  | "DISPUTED"
  | "RESOLVED";

type CustomerBooking = {
  id: string;
  providerId: string;
  providerName: string;
  serviceId: string;
  serviceName: string;
  stylistId?: string | null;
  stylistName?: string | null;
  slotId: string;
  startTime: string;
  endTime: string;
  currentStatus: BookingStatus;
  totalAmount: number;
  currency?: string;
  bookingSource?: string;
  notes?: string | null;
  depositAmount?: number | null;
  createdAt?: string;
};

type CustomerBookingsResponse = {
  bookings: CustomerBooking[];
};

const API_BASE = process.env.REACT_APP_API_BASE_URL ?? "";

const STATUS_LABELS: Record<BookingStatus, string> = {
  REQUESTED: "Requested",
  AWAITING_ACCEPTANCE: "Awaiting acceptance",
  ACCEPTED: "Accepted",
  AUTOACCEPTED: "Auto-accepted",
  REJECTED: "Rejected",
  CANCELLED_BY_CUSTOMER: "Cancelled by customer",
  CANCELLED_BY_PROVIDER: "Cancelled by provider",
  IN_SERVICE: "In service",
  COMPLETED_PENDING_CONFIRMATION: "Completed, awaiting confirmation",
  COMPLETED_ASSUMED: "Completed (assumed)",
  COMPLETED_CONFIRMED: "Completed",
  NO_SHOW: "No-show",
  DISPUTED: "Disputed",
  RESOLVED: "Resolved",
};

function formatMoney(amount: number, currency = "GBP") {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(amount);
}

function formatDateTime(value: string) {
  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function isUpcoming(status: BookingStatus, startTime: string) {
  const activeStatuses: BookingStatus[] = [
    "REQUESTED",
    "AWAITING_ACCEPTANCE",
    "ACCEPTED",
    "AUTOACCEPTED",
    "IN_SERVICE",
  ];

  return activeStatuses.includes(status) && new Date(startTime).getTime() >= Date.now();
}

export default function CustomerBookingsPage() {
  const [bookings, setBookings] = useState<CustomerBooking[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<"ALL" | "UPCOMING" | "PAST">("ALL");

  useEffect(() => {
    let isMounted = true;

    async function loadBookings() {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(`${API_BASE}/api/bookings/me`, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        });

        const data = (await response.json().catch(() => null)) as CustomerBookingsResponse | null;

        if (!response.ok) {
          throw new Error("Unable to load your bookings.");
        }

        if (isMounted) {
          setBookings(data?.bookings ?? []);
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : "Unable to load your bookings.");
          setBookings([]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    void loadBookings();

    return () => {
      isMounted = false;
    };
  }, []);

  const filteredBookings = useMemo(() => {
    if (statusFilter === "ALL") return bookings;

    if (statusFilter === "UPCOMING") {
      return bookings.filter((booking) => isUpcoming(booking.currentStatus, booking.startTime));
    }

    return bookings.filter((booking) => !isUpcoming(booking.currentStatus, booking.startTime));
  }, [bookings, statusFilter]);

  return (
    <section style={{ padding: "24px" }}>
      <header style={{ marginBottom: "24px" }}>
        <h1 style={{ marginBottom: "8px" }}>My bookings</h1>
        <p style={{ margin: 0, color: "#666" }}>
          View upcoming appointments, completed services, and booking history.
        </p>
      </header>

      <div style={{ display: "flex", gap: "12px", marginBottom: "24px", flexWrap: "wrap" }}>
        <button type="button" onClick={() => setStatusFilter("ALL")}>
          All
        </button>
        <button type="button" onClick={() => setStatusFilter("UPCOMING")}>
          Upcoming
        </button>
        <button type="button" onClick={() => setStatusFilter("PAST")}>
          Past
        </button>
      </div>

      {isLoading ? <p>Loading bookings…</p> : null}
      {error ? <p style={{ color: "#b00020" }}>{error}</p> : null}

      {!isLoading && !error && filteredBookings.length === 0 ? (
        <div
          style={{
            border: "1px solid #ddd",
            borderRadius: "12px",
            padding: "24px",
            background: "#fafafa",
          }}
        >
          <h2 style={{ marginTop: 0 }}>No bookings found</h2>
          <p style={{ marginBottom: 0 }}>
            Your appointments will appear here once you book a service.
          </p>
        </div>
      ) : null}

      <div style={{ display: "grid", gap: "16px" }}>
        {filteredBookings.map((booking) => (
          <article
            key={booking.id}
            style={{
              border: "1px solid #ddd",
              borderRadius: "12px",
              padding: "20px",
              background: "#fff",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "16px",
                flexWrap: "wrap",
                marginBottom: "12px",
              }}
            >
              <div>
                <h2 style={{ margin: "0 0 6px 0", fontSize: "1.1rem" }}>{booking.serviceName}</h2>
                <p style={{ margin: 0, color: "#555" }}>{booking.providerName}</p>
              </div>

              <span
                style={{
                  alignSelf: "flex-start",
                  padding: "6px 10px",
                  borderRadius: "999px",
                  background: "#f3f4f6",
                  fontSize: "0.9rem",
                }}
              >
                {STATUS_LABELS[booking.currentStatus]}
              </span>
            </div>

            <dl
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                gap: "12px",
                margin: 0,
              }}
            >
              <div>
                <dt style={{ fontWeight: 600 }}>Appointment</dt>
                <dd style={{ margin: "4px 0 0 0" }}>{formatDateTime(booking.startTime)}</dd>
              </div>

              <div>
                <dt style={{ fontWeight: 600 }}>Ends</dt>
                <dd style={{ margin: "4px 0 0 0" }}>{formatDateTime(booking.endTime)}</dd>
              </div>

              <div>
                <dt style={{ fontWeight: 600 }}>Stylist</dt>
                <dd style={{ margin: "4px 0 0 0" }}>{booking.stylistName || "To be assigned"}</dd>
              </div>

              <div>
                <dt style={{ fontWeight: 600 }}>Amount</dt>
                <dd style={{ margin: "4px 0 0 0" }}>
                  {formatMoney(booking.totalAmount, booking.currency || "GBP")}
                </dd>
              </div>

              <div>
                <dt style={{ fontWeight: 600 }}>Deposit</dt>
                <dd style={{ margin: "4px 0 0 0" }}>
                  {booking.depositAmount != null
                    ? formatMoney(booking.depositAmount, booking.currency || "GBP")
                    : "Not applicable"}
                </dd>
              </div>

              <div>
                <dt style={{ fontWeight: 600 }}>Source</dt>
                <dd style={{ margin: "4px 0 0 0" }}>{booking.bookingSource || "WEB"}</dd>
              </div>
            </dl>

            {booking.notes ? (
              <div style={{ marginTop: "16px" }}>
                <strong>Notes:</strong>
                <p style={{ margin: "6px 0 0 0" }}>{booking.notes}</p>
              </div>
            ) : null}
          </article>
        ))}
      </div>
    </section>
  );
}